/**
 * Panel de contenido educativo: los "apuntes" de una estación.
 *
 * Presenta los segmentos de un `ContentSpec` tal cual vienen del OA original
 * (textos, listas, tablas, figuras con su crédito, enlaces y pasos). Es de
 * sólo lectura y reconsultable: cerrarlo avisa al motor para que registre la
 * información como presentada. Reutiliza el marco visual de los minijuegos
 * para no duplicar estilos; el cuerpo sí desplaza cuando el contenido es largo.
 */

import type { ContentSegment, ContentSpec, GameDefinition } from '../types/game';
import { button, el, on, type El } from './dom';

export interface ContentPanelCallbacks {
  onClose: () => void;
  onSfx?: (name: string) => void;
}

export class ContentPanel {
  private root: El<'div'> | null = null;
  private offEscape: (() => void) | null = null;

  constructor(
    private readonly host: HTMLElement,
    private readonly spec: ContentSpec,
    private readonly game: GameDefinition,
    private readonly assetBaseUrl: string,
    private readonly callbacks: ContentPanelCallbacks
  ) {}

  open(): void {
    if (this.root) return;
    this.root = el('div', {
      className: 'ae-mg-overlay',
      attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': this.spec.titulo }
    });

    const panel = el('div', { className: 'ae-mg-panel ae-content-panel' });
    panel.append(
      el('div', { className: 'ae-mg-header' }, [
        el('h2', { className: 'ae-mg-title', textContent: this.spec.titulo }),
        button('✕', () => this.close(), { className: 'ae-mg-close', ariaLabel: 'Cerrar' })
      ])
    );

    const body = el('div', { className: 'ae-content-body' });
    for (const segment of this.spec.segmentos) {
      body.append(this.renderSegment(segment));
    }
    panel.append(body);

    panel.append(
      el('div', { className: 'ae-mg-actions' }, [
        button('Entendido', () => this.close(), { className: 'ae-btn-primary' })
      ])
    );

    this.root.append(panel);
    this.host.append(this.root);
    this.offEscape = on(document, 'keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') this.close();
    });
  }

  private close(): void {
    if (!this.root) return;
    this.callbacks.onSfx?.('pop');
    this.destroy();
    this.callbacks.onClose();
  }

  destroy(): void {
    this.offEscape?.();
    this.offEscape = null;
    this.root?.remove();
    this.root = null;
  }

  private renderSegment(segment: ContentSegment): HTMLElement {
    switch (segment.kind) {
      case 'heading':
        return el('h3', { className: 'ae-content-heading', textContent: segment.text });
      case 'text':
        return el('p', { className: 'ae-content-text', textContent: segment.text });
      case 'list': {
        const list = el(segment.ordered ? 'ol' : 'ul', { className: 'ae-content-list' });
        for (const item of segment.items) list.append(el('li', { textContent: item }));
        return list;
      }
      case 'callout': {
        const tone = segment.tone ?? 'info';
        const box = el('div', { className: `ae-content-callout ae-content-callout-${tone}` });
        if (segment.title) box.append(el('strong', { textContent: segment.title }));
        box.append(el('span', { textContent: segment.text }));
        return box;
      }
      case 'table': {
        const wrap = el('div', { className: 'ae-content-table-wrap' });
        const table = el('table', { className: 'ae-content-table' });
        if (segment.headers && segment.headers.length > 0) {
          const thead = el('thead');
          const tr = el('tr');
          for (const header of segment.headers) tr.append(el('th', { textContent: header }));
          thead.append(tr);
          table.append(thead);
        }
        const tbody = el('tbody');
        for (const row of segment.rows) {
          const tr = el('tr');
          for (const cell of row) tr.append(el('td', { textContent: cell }));
          tbody.append(tr);
        }
        table.append(tbody);
        wrap.append(table);
        if (segment.caption) wrap.append(el('div', { className: 'ae-content-caption', textContent: segment.caption }));
        return wrap;
      }
      case 'figure': {
        const wrap = el('div', { className: 'ae-content-figure' });
        const src = this.imageSrc(segment.image);
        if (src) {
          wrap.append(el('img', { src, alt: segment.caption ?? segment.image ?? 'Figura' }));
        }
        if (segment.caption) wrap.append(el('div', { className: 'ae-content-caption', textContent: segment.caption }));
        if (segment.credit) wrap.append(el('div', { className: 'ae-content-credit', textContent: segment.credit }));
        return wrap;
      }
      case 'link': {
        const a = el('a', {
          className: 'ae-content-link',
          href: segment.url,
          textContent: segment.text,
          target: '_blank',
          rel: 'noopener noreferrer'
        });
        return el('p', {}, [a]);
      }
      case 'steps': {
        const list = el('ol', { className: 'ae-content-steps' });
        for (const step of segment.steps) {
          list.append(
            el('li', {}, [
              el('strong', { textContent: step.title }),
              el('span', { textContent: ` — ${step.text}` })
            ])
          );
        }
        return list;
      }
    }
  }

  private imageSrc(key: string | undefined): string | undefined {
    if (!key) return undefined;
    const img = this.game.assets.images?.[key];
    if (!img) return undefined;
    return img.src.startsWith('data:') ? img.src : this.assetBaseUrl + img.src;
  }
}
